import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, X, Sparkles } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function PremiumSuccess() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { setLocalPremiumFlag, refresh } = useAuth();
    const [status, setStatus] = useState("pending"); // pending | paid | failed | expired
    const attempts = useRef(0);

    useEffect(() => {
        const session_id = params.get("session_id");
        if (!session_id) {
            navigate("/", { replace: true });
            return;
        }

        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            attempts.current += 1;
            try {
                const { data } = await axios.get(`${API}/checkout/status/${session_id}`, {
                    withCredentials: true,
                });
                if (data.payment_status === "paid") {
                    setLocalPremiumFlag(true);
                    setStatus("paid");
                    refresh();
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
            } catch (e) {
                console.warn("poll error", e);
            }
            if (attempts.current >= MAX_ATTEMPTS) {
                setStatus("failed");
                return;
            }
            setTimeout(poll, POLL_INTERVAL_MS);
        };
        poll();

        return () => { cancelled = true; };
    }, [params, navigate, setLocalPremiumFlag, refresh]);

    return (
        <div className="min-h-screen w-full bg-app overflow-hidden flex items-center justify-center relative" data-testid="premium-success-screen">
            <div className="absolute inset-0 aurora-wash opacity-50 pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />

            <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="relative z-10 max-w-md w-full px-6 text-center"
            >
                {status === "pending" && (
                    <>
                        <div className="mx-auto h-14 w-14 rounded-full border border-app/30 bg-surface-2 flex items-center justify-center mb-6">
                            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                                <Sparkles className="w-5 h-5 text-app" strokeWidth={1.5} />
                            </motion.div>
                        </div>
                        <h2 className="font-serif italic text-app text-3xl mb-2">confirming your payment…</h2>
                        <p className="font-sans text-sm text-app-3">This takes a few seconds.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <motion.div
                            initial={{ scale: 0.8 }} animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200, damping: 14 }}
                            className="mx-auto h-16 w-16 rounded-full border border-app/40 bg-app/10 flex items-center justify-center mb-6"
                        >
                            <Check className="w-6 h-6 text-app" strokeWidth={2} />
                        </motion.div>
                        <h2 className="font-serif italic text-app text-4xl mb-3">welcome to premium.</h2>
                        <p className="font-sans text-base text-app-2 leading-relaxed mb-8 max-w-[32ch] mx-auto">
                            Watermark removed. Animation unlocked. All platform sizes available.
                        </p>
                        <button
                            data-testid="premium-success-continue"
                            onClick={() => navigate("/")}
                            className="px-8 py-4 rounded-full bg-cta text-app-inverse font-sans font-medium text-sm tracking-wide hover:opacity-90 transition-opacity"
                        >
                            Make a new MoodCard
                        </button>
                    </>
                )}
                {(status === "failed" || status === "expired") && (
                    <>
                        <div className="mx-auto h-14 w-14 rounded-full border border-app/30 bg-surface-2 flex items-center justify-center mb-6">
                            <X className="w-5 h-5 text-app-2" strokeWidth={1.5} />
                        </div>
                        <h2 className="font-serif italic text-app text-3xl mb-2">
                            {status === "expired" ? "Session expired." : "Something didn't go through."}
                        </h2>
                        <p className="font-sans text-sm text-app-3 mb-8">No charge was made. You can try again.</p>
                        <button
                            onClick={() => navigate("/")}
                            className="px-6 py-3 rounded-full border border-app/30 text-app hover:bg-surface-2 transition-colors font-sans text-sm"
                        >
                            Back to MoodCard
                        </button>
                    </>
                )}
            </motion.div>
        </div>
    );
}
